import urls from "global/constants/UrlConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { openSuccessNotification } from "helpers/methods";
import { CampaignSegments, SubmitCampaign } from "types/Campaign";
import history from "utils/history";
import {
  getCallParams,
  getFileCallParams,
  getSearchedCallParams,
  makeCall,
} from "utils/service";

export const spamCheck = async (emailPlainText: string) => {
  try {
    const callParams = await getCallParams("POST", emailPlainText);
    const response: any = await makeCall(urls.SPAM_CHECK, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const subjectLineGeneration = async (emailPlainText: string) => {
  try {
    const callParams = await getCallParams("POST", emailPlainText);
    const response: any = await makeCall(
      urls.SUBJECT_LINE_GENERATION,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const recipientCount = async (audienceType: number, ids: number[]) => {
  try {
    const url = `${urls.RECIPIENT_COUNT}/${audienceType}/count`;
    const callParams = await getCallParams("POST", ids);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const sendTime = async (campaignSegment: CampaignSegments) => {
  try {
    const url = `${urls.CAMPAIGN_SEND_TIME}`;
    const callParams = await getCallParams("POST", campaignSegment);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const allowedEmailCounts = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.SELF_CAMPAIGNER, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const submitRegularCampaign = async (
  object: SubmitCampaign,
  scheduleTime: string
) => {
  try {
    const body = {
      ...object,
      campaign: {
        ...object.campaign,
        content: object.campaign.content,
        status: "Submitted",
        contentJson: JSON.stringify(object.campaign.contentJson),
        scheduleTime,
      },
      plainTextOfCampaignContent: [object.campaign.contentPlain],
    };
    delete body.abTestDetails;
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(
      urls.SUBMIT_REGULAR_CAMPAIGN,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const updateRegularCampaign = async (
  object: SubmitCampaign,
  scheduleTime: string
) => {
  try {
    const body = {
      ...object,
      campaign: {
        ...object.campaign,
        content: object.campaign.content,
        status: "Submitted",
        contentJson: JSON.stringify(object.campaign.contentJson),
        scheduleTime,
      },
      plainTextOfCampaignContent: [object.campaign.contentPlain],
    };
    delete body.abTestDetails;
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(
      urls.UPDATE_REGULAR_CAMPAIGN,
      callParams
    );
    history.push(
      `${viewpaths.campaignsViewPath}?page=1&rowsPerPage=10&currentTab=email`
    );
    openSuccessNotification("Your campaign update succefully.");
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const submitRetargetCampaign = async (
  object: SubmitCampaign,
  scheduleTime: string,
  opened: boolean,
  groupType: number,
  status: string
) => {
  try {
    const body = {
      ...object,
      campaign: {
        ...object.campaign,
        content: object.campaign.content,
        status: status,
        contentJson: JSON.stringify(object.campaign.contentJson),
        scheduleTime,
      },
      plainTextOfCampaignContent: [object.campaign.contentPlain],
    };
    delete body.abTestDetails;
    const url = `${urls.SUBMIT_RETARGET_AUDIENCE_REGULAR_CAMPAIGN}/groupType/${groupType}/${opened}`;
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const submitABCampaign = async (object: SubmitCampaign) => {
  try {
    const body = {
      ...object,
      campaign: {
        ...object.campaign,
        status: "Submitted",
        scheduleTime: null,
        content: object.campaign.content,
        contentJson: JSON.stringify(object.campaign.contentJson),
      },
    };
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(urls.SUBMIT_AB_CAMPAIGN, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const draftABCampaign = async (object: SubmitCampaign) => {
  try {
    const body = {
      ...object,
      campaign: {
        ...object.campaign,
        status: object.campaign.status ?? "Draft",
        scheduleTime: null,
        content: object.campaign.content,
        contentJson: JSON.stringify(object.campaign.contentJson),
      },
    };
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(urls.DRAFT_AB_CAMPAIGN, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const draftCampaign = async (
  object: SubmitCampaign,
  scheduleTime: string
) => {
  try {
    const body = {
      ...object,
      campaign: {
        ...object.campaign,
        status: object.campaign.status ?? "Draft",
        content: object.campaign.content,
        contentJson: JSON.stringify(object.campaign.contentJson),
        scheduleTime,
      },
      plainTextOfCampaignContent: [object.campaign.contentPlain],
    };
    delete body.abTestDetails;
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(
      urls.DRAFT_REGULAR_CAMPAIGN,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchAllCampaigns = async (
  page: number,
  limit: number,
  body: any
) => {
  const url = `${urls.CAMPAIGNS_LIST}/${page}/limit/${limit}`;
  try {
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchCampaignCounts = async (body: any) => {
  const url = `${urls.CAMPAIGNS_LIST_COUNT}`;
  try {
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchAllSocialCampaigns = async (
  page: number,
  limit: number,
  body: any
) => {
  const url = `${urls.SOCIAL_CAMPAIGNS_LIST}/${page}/limit/${limit}`;
  try {
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchSocialCampaignCounts = async (body: any) => {
  const url = `${urls.SOCIAL_CAMPAIGNS_LIST_COUNT}`;
  try {
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchEmailContent = async (id: number) => {
  const url = `${urls.VIEW_EMAIL_CAMPAIGN_CONTENT}/${id}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getLinkActivity = async (id: number) => {
  try {
    const url = `${urls.VIEW_EMAIL_LINK_ACTIVITY}/${id}`;
    const callParams = await getCallParams("POST");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getWholeCampaignDetails = async (id: number) => {
  const url = `${urls.FULL_CAMPAIGN_DETAILS}/${id}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getDraftABCampaignDetails = async (id: number) => {
  const url = `${urls.FULL_AB_CAMPAIGN_DETAILS}/${id}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchChildCampaign = async (id: number) => {
  const url = `${urls.CHILD_CAMPAIGN_DETAILS}/${id}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const renameCampaign = async (id: number, name: string) => {
  const url = `${urls.RENAME_CAMPAIGN}/${id}`;
  try {
    const callParams = await getSearchedCallParams("POST", name);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const deletecampaignById = async (id: number) => {
  const url = `${urls.DELETE_CAMPAIGN}/${id}`;
  try {
    const callParams = await getCallParams("DELETE");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchUserActions = async (
  campaignId: number,
  metricId: number,
  page: number,
  limit: number
) => {
  const url = `${urls.OPEN_CLICK_LIST}/${campaignId}/metrics/${metricId}/page/${page}/limit/${limit}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchUserActionsCounts = async (
  campaignId: number,
  metricId: number
) => {
  const url = `${urls.OPEN_CLICK_LIST_COUNT}/${campaignId}/metrics/${metricId}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchFailedUserActions = async (
  campaignId: number,
  metricId: number,
  page: number,
  limit: number
) => {
  const url = `${urls.FAILED_LIST}/${campaignId}/metrics/${metricId}/page/${page}/limit/${limit}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchFailedNewUserActions = async (
  campaignId: number,
  metricId: number,
  page: number,
  limit: number
) => {
  const url = `${urls.FETCH_FAILED_RESPONSE_LIST}/${campaignId}/page/${page}/limit/${limit}`;
  try {
    const callParams = await getCallParams("POST");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getRetargetedGroupId = async (
  campaignId: string,
  opened: boolean
) => {
  const url = `${urls.retargetedGroup}/${campaignId}/group/3/${opened}`;
  try {
    const callParams = await getCallParams("POST");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getFollowUpGroupId = async (
  campaignId: string,
  followUp: boolean
) => {
  const url = `${urls.retargetedGroup}/${campaignId}/group/5/${followUp}`;
  try {
    const callParams = await getCallParams("POST");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const downloadOpenedClickReport = async (
  requestId: any,
  userTimeZone: any,
  actions: string[]
) => {
  const url = `${urls.DOWNLOAD_CAMPAIGN_REPORT}/${requestId}`;
  try {
    const body = {
      userTimeZone: userTimeZone,
      userActions: actions,
    };
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getCampaignsPerformanceData = async (
  campaignId: number,
  body: any
) => {
  try {
    const url = `${urls.CAMPAIGN_PERFORMANCE_DATA}/${campaignId}/dates`;
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const submitSocialCampaign = async (
  payload: any,
  scheduleTime: string
) => {
  const body = {
    ...payload,
    scheduleTime,
  };
  try {
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(urls.sendSocialCampaign, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchFailedUserActionsCounts = async (
  campaignId: number,
  metricId: number
) => {
  const url = `${urls.FAILED_LIST_COUNT}/${campaignId}/metrics/${metricId}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchApprovedDomains = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(urls.FETCH_APPROVED_DOMAINS, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const sendTestEmails = async (object: SubmitCampaign) => {
  try {
    const body = {
      ...object,
      campaign: {
        ...object.campaign,
        status: "Submitted",
        content: object.campaign.content,
        scheduleTime: null,
        contentJson: JSON.stringify(object.campaign.contentJson),
      },
      plainTextOfCampaignContent: [object.campaign.contentPlain],
    };
    delete body.abTestDetails;
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(
      urls.TEST_REGULAR_CAMPAIGN,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const runSequentialCampaigns = async (campaign: SubmitCampaign[]) => {
  try {
    const body = campaign.map((object) => {
      const returnObject = {
        ...object,
        campaign: {
          ...object.campaign,
          status: "Submitted",
          content: object.campaign.content,
          contentJson: JSON.stringify(object.campaign.contentJson),
        },
        plainTextOfCampaignContent: [object.campaign.contentPlain],
      };
      delete returnObject.abTestDetails;
      return returnObject;
    });
    const callParams = await getCallParams("POST", body);
    const response: any = await makeCall(
      urls.SUBMIT_SEQUENTIAL_CAMPAIGN,
      callParams
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const attachFileUpload = async (formData: any) => {
  try {
    const callParams = await getFileCallParams("POST", formData);
    const response: any = await makeCall(urls.attachFileUpload, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchFailedResponse = async (id: number) => {
  try {
    const url = `${urls.FETCH_FAILED_RESPONSE}/${id}`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchFailedResponseNew = async (id: number) => {
  try {
    const url = `${urls.FETCH_FAILED_RESPONSE_COUNT}/${id}`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const updateResultManually = async (
  campaignId: string,
  scheduleTime: any
) => {
  try {
    const body = {
      id: campaignId,
      scheduleTime: scheduleTime,
    };
    const url = `${urls.updateABTestResultManually}`;
    const callParams = await getCallParams("PATCH", body);
    return await makeCall(url, callParams);
  } catch (error) {
    throw error;
  }
};

export const convertToManual = async (campaignId: number) => {
  try {
    const body = {
      id: campaignId,
    };
    const url = `${urls.convertABTestCampaignToManual}`;
    const callParams = await getCallParams("PATCH", body);
    return await makeCall(url, callParams);
  } catch (error) {
    throw error;
  }
};

export const duplicateCampaign = async (campaignId: number, object: any) => {
  const url = `${urls.DUPLICATE_CAMPAIGN}/${campaignId}`;
  try {
    const callParams = await getCallParams("POST", object);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};
